import React, { useState, useEffect } from 'react';
import { webhooksService } from '../../services/webhooksService';
import { standingsService } from '../../services/standingsService';
import { scheduleService } from '../../services/scheduleService';
import { logsService } from '../../services/logsService';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useSeasonContext } from '../../context/SeasonContext';
import '../../styles/WebhooksManager.css';

const WebhooksManager = () => {
  const { user } = useAdminAuth();
  const { selectedSeason, availableSeasons, setSelectedSeason } = useSeasonContext();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showForm, setShowForm] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(null);
  const [showMessageSender, setShowMessageSender] = useState(false);
  const [currentTab, setCurrentTab] = useState('webhooks');
  const [sendingRankings, setSendingRankings] = useState(false);
  const [sendingSchedule, setSendingSchedule] = useState(false);
  const [sendingScores, setSendingScores] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedTab, setSelectedTab] = useState('all');

  const eventTypes = [
    { value: 'news_published', label: 'News Published', color: '#1e88e5' },
    { value: 'rankings_updated', label: 'Rankings Updated', color: '#ffa500' },
    { value: 'schedule_updated', label: 'Schedule Updated', color: '#9c27b0' },
    { value: 'scores_updated', label: 'Scores Updated', color: '#4caf50' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'discord',
    eventTypes: ['news_published'],
    channel: '',
    isMediaWebhook: false
  });

  const [messageSenderData, setMessageSenderData] = useState({
    webhookId: null,
    eventType: 'news_published',
    title: '',
    description: '',
    fields: [],
    image: '',
    editMessageId: null
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  useEffect(() => {
    const loadWeeks = async () => {
      if (!selectedSeason) {
        setAvailableWeeks([]);
        setSelectedWeek(null);
        return;
      }

      try {
        const games = await scheduleService.getScheduleBySeason(selectedSeason.id);
        const weeks = [...new Set(games.map(g => g.week))].sort((a, b) => a - b);
        setAvailableWeeks(weeks);
        if (weeks.length > 0 && !selectedWeek) {
          setSelectedWeek(weeks[0]);
        }
      } catch (err) {
        console.error('Error loading weeks:', err);
        setAvailableWeeks([]);
      }
    };

    loadWeeks();
  }, [selectedSeason]);

  const loadWebhooks = async () => {
    setLoading(true);
    const result = await webhooksService.getWebhooks();
    if (result.success) {
      setWebhooks(result.webhooks);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEventTypeChange = (eventType) => {
    setFormData(prev => ({
      ...prev,
      eventTypes: prev.eventTypes.includes(eventType)
        ? prev.eventTypes.filter(e => e !== eventType)
        : [...prev.eventTypes, eventType]
    }));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageSenderData.webhookId) {
      setMessage({ type: 'error', text: 'Please select a webhook' });
      return;
    }

    if (!messageSenderData.title) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        title: messageSenderData.title,
        description: messageSenderData.description,
        author: user?.email || 'Admin',
        image: messageSenderData.image || undefined,
        fields: messageSenderData.fields.filter(f => f.name && f.value) || undefined
      };

      const result = await webhooksService.sendWebhookMessage(
        messageSenderData.webhookId,
        messageSenderData.eventType,
        payload,
        messageSenderData.editMessageId
      );

      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: messageSenderData.editMessageId 
            ? 'Message updated successfully' 
            : 'Message sent successfully'
        });
        resetMessageSender();
        loadWebhooks();
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send message' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    setMessageSenderData(prev => ({
      ...prev,
      fields: [...prev.fields, { name: '', value: '' }]
    }));
  };

  const handleRemoveField = (index) => {
    setMessageSenderData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleFieldChange = (index, key, value) => {
    setMessageSenderData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [key]: value } : f
      )
    }));
  };

  const openMessageSender = async (webhookId, eventType) => {
    const lastMessage = await webhooksService.getLastMessageForEvent(webhookId, eventType);
    
    setMessageSenderData({
      webhookId,
      eventType,
      title: '',
      description: '',
      fields: [],
      image: '',
      editMessageId: lastMessage.success && lastMessage.log ? lastMessage.log.message_id : null
    });
    setShowMessageSender(true);
  };

  const resetMessageSender = () => {
    setMessageSenderData({
      webhookId: null,
      eventType: 'news_published',
      title: '',
      description: '',
      fields: [],
      image: '',
      editMessageId: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url) {
      setMessage({ type: 'error', text: 'Name and URL are required' });
      return;
    }

    setLoading(true);
    
    try {
      const result = selectedWebhookId
        ? await webhooksService.updateWebhook(selectedWebhookId, formData)
        : await webhooksService.createWebhook(
            formData.name,
            formData.url,
            formData.type,
            formData.eventTypes,
            user?.id,
            formData.channel,
            formData.isMediaWebhook
          );

      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: selectedWebhookId ? 'Webhook updated successfully' : 'Webhook created successfully' 
        });
        
        await logsService.createLog(
          user?.id,
          user?.email,
          selectedWebhookId ? 'update_webhook' : 'create_webhook',
          { webhook: result.webhook }
        );

        loadWebhooks();
        resetForm();
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (webhook) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      type: webhook.type,
      eventTypes: webhook.event_types.split(','),
      channel: webhook.channel || '',
      isMediaWebhook: webhook.is_media_webhook || false
    });
    setSelectedWebhookId(webhook.id);
    setShowForm(true);
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;

    setLoading(true);
    const result = await webhooksService.deleteWebhook(webhookId);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Webhook deleted successfully' });
      await logsService.createLog(user?.id, user?.email, 'delete_webhook', { webhook_id: webhookId });
      loadWebhooks();
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    setLoading(false);
  };

  const handleTestWebhook = async (webhookId) => {
    setTesting(webhookId);
    try {
      const result = await webhooksService.testWebhook(webhookId);
      
      if (result.success) {
        setMessage({ type: 'success', text: `Test successful (Status: ${result.status})` });
      } else {
        const errorMsg = result.error || 'Unknown error';
        setMessage({ type: 'error', text: `Test failed: ${errorMsg}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Test error: ${error?.message || 'Unknown error'}` });
    } finally {
      setTesting(null);
      loadWebhooks();
    }
  };

  const handleViewLogs = async (webhookId) => {
    const result = await webhooksService.getWebhookLogs(webhookId);
    if (result.success) {
      setLogs(result.logs);
      setSelectedWebhookId(webhookId);
      setShowLogs(true);
    }
  };

  const toggleWebhookStatus = async (webhook) => {
    setLoading(true);
    const result = await webhooksService.updateWebhook(webhook.id, {
      is_active: !webhook.is_active
    });

    if (result.success) {
      await logsService.createLog(
        user?.id,
        user?.email,
        'toggle_webhook_status',
        { webhook_id: webhook.id, is_active: !webhook.is_active }
      );
      loadWebhooks();
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      type: 'discord',
      eventTypes: ['news_published'],
      channel: '',
      isMediaWebhook: false
    });
    setSelectedWebhookId(null);
    setShowForm(false);
  };

  const calculateWinPercentage = (team) => {
    if (team.wins + team.losses === 0) return '0.000';
    const pct = team.wins / (team.wins + team.losses);
    return pct.toFixed(3);
  };

  const sortAndRankStandings = (teams) => {
    const sorted = [...teams].sort((a, b) => {
      const aWinPct = a.wins + a.losses === 0 ? 0 : a.wins / (a.wins + a.losses);
      const bWinPct = b.wins + b.losses === 0 ? 0 : b.wins / (b.wins + b.losses);

      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }

      if (bWinPct !== aWinPct) {
        return bWinPct - aWinPct;
      }

      return b.points_for - a.points_for;
    });

    return sorted.map((team, index) => ({
      ...team,
      calculatedRank: index + 1,
    }));
  };

  const formatStandingsForWebhook = (standings, season, tab) => {
    const title = tab === 'all' 
      ? `${season.name} - All Teams Standings`
      : `${season.name} - Standings`;
    
    const fields = standings.map(team => ({
      name: `${team.calculatedRank}. ${team.teams.name}`,
      value: `W: ${team.wins} | L: ${team.losses} | PCT: ${calculateWinPercentage(team)} | PF: ${team.points_for} | PA: ${team.points_against}`,
      inline: false
    }));

    return {
      title: title,
      description: `Updated: ${new Date().toLocaleString()}`,
      fields: fields
    };
  };

  const formatScheduleForWebhook = (games, season, weekFilter = null) => {
    let filteredGames = games;
    if (weekFilter) {
      filteredGames = games.filter(game => game.week === weekFilter);
    }

    const gamesByWeek = {};
    filteredGames.forEach(game => {
      if (!gamesByWeek[game.week]) {
        gamesByWeek[game.week] = [];
      }
      gamesByWeek[game.week].push(game);
    });

    const fields = [];
    Object.keys(gamesByWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach(week => {
        gamesByWeek[week].forEach(game => {
          const team1 = game.team1?.name || `Team ${game.team1_id}`;
          const team2 = game.team2?.name || `Team ${game.team2_id}`;
          const date = game.scheduled_date 
            ? new Date(game.scheduled_date).toLocaleDateString()
            : 'TBD';
          fields.push({
            name: `${team1} vs ${team2}`,
            value: `Scheduled: ${date}`,
            inline: false
          });
        });
      });

    const titleSuffix = weekFilter ? ` - Week ${weekFilter}` : '';
    return {
      title: `${season.name} - Schedule${titleSuffix}`,
      description: `Updated: ${new Date().toLocaleString()}`,
      fields: fields
    };
  };

  const formatScoresForWebhook = (games, season, weekFilter = null) => {
    let completedGames = games.filter(game => game.is_completed && game.game_scores?.length > 0);
    
    if (weekFilter) {
      completedGames = completedGames.filter(game => game.week === weekFilter);
    }
    
    const gamesByWeek = {};
    completedGames.forEach(game => {
      if (!gamesByWeek[game.week]) {
        gamesByWeek[game.week] = [];
      }
      gamesByWeek[game.week].push(game);
    });

    const fields = [];
    Object.keys(gamesByWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach(week => {
        gamesByWeek[week].forEach(game => {
          const team1 = game.team1?.name || `Team ${game.team1_id}`;
          const team2 = game.team2?.name || `Team ${game.team2_id}`;
          const score = game.game_scores?.[0];
          const team1Score = score?.team1_score || 0;
          const team2Score = score?.team2_score || 0;
          
          fields.push({
            name: `${team1} vs ${team2}`,
            value: `Final: ${team1Score} - ${team2Score}`,
            inline: false
          });
        });
      });

    const titleSuffix = weekFilter ? ` - Week ${weekFilter}` : '';
    return {
      title: `${season.name} - Scores${titleSuffix}`,
      description: `Updated: ${new Date().toLocaleString()}`,
      fields: fields
    };
  };

  const handleSendRankings = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    setSendingRankings(true);
    setMessage({ type: '', text: '' });

    try {
      let standings;
      if (selectedTab === 'all') {
        standings = await standingsService.getStandingsBySeason(selectedSeason.id);
      } else {
        standings = [];
      }

      if (!standings || standings.length === 0) {
        setMessage({ type: 'error', text: 'No standings data available' });
        setSendingRankings(false);
        return;
      }

      const rankedStandings = sortAndRankStandings(standings);
      const payload = formatStandingsForWebhook(rankedStandings, selectedSeason, selectedTab);

      const rankingsWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('rankings_updated')
      );

      if (rankingsWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for rankings updates' });
        setSendingRankings(false);
        return;
      }

      let successCount = 0;
      for (const webhook of rankingsWebhooks) {
        const result = await webhooksService.sendWebhookMessage(webhook.id, 'rankings_updated', payload);
        if (result.success) successCount++;
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Rankings sent to ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'Failed to send rankings to webhooks' });
      }
    } catch (err) {
      console.error('Error sending rankings:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingRankings(false);
    }
  };

  const handleUpdateRankings = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    setSendingRankings(true);
    setMessage({ type: '', text: '' });

    try {
      let standings;
      if (selectedTab === 'all') {
        standings = await standingsService.getStandingsBySeason(selectedSeason.id);
      } else {
        standings = [];
      }

      if (!standings || standings.length === 0) {
        setMessage({ type: 'error', text: 'No standings data available' });
        setSendingRankings(false);
        return;
      }

      const rankedStandings = sortAndRankStandings(standings);
      const payload = formatStandingsForWebhook(rankedStandings, selectedSeason, selectedTab);

      const rankingsWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('rankings_updated')
      );

      if (rankingsWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for rankings updates' });
        setSendingRankings(false);
        return;
      }

      let successCount = 0;
      for (const webhook of rankingsWebhooks) {
        const lastMessage = await webhooksService.getLastMessageForEvent(webhook.id, 'rankings_updated');
        if (lastMessage.success && lastMessage.log?.message_id) {
          const result = await webhooksService.sendWebhookMessage(
            webhook.id, 
            'rankings_updated', 
            payload, 
            lastMessage.log.message_id
          );
          if (result.success) successCount++;
        }
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Rankings updated on ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'No previous messages found to update' });
      }
    } catch (err) {
      console.error('Error updating rankings:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingRankings(false);
    }
  };

  const handleSendSchedule = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    if (!selectedWeek) {
      setMessage({ type: 'error', text: 'Please select a week first' });
      return;
    }

    setSendingSchedule(true);
    setMessage({ type: '', text: '' });

    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);

      if (!games || games.length === 0) {
        setMessage({ type: 'error', text: 'No schedule data available' });
        setSendingSchedule(false);
        return;
      }

      const payload = formatScheduleForWebhook(games, selectedSeason, selectedWeek);

      const scheduleWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('schedule_updated')
      );

      if (scheduleWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for schedule updates' });
        setSendingSchedule(false);
        return;
      }

      let successCount = 0;
      for (const webhook of scheduleWebhooks) {
        const result = await webhooksService.sendWebhookMessage(webhook.id, 'schedule_updated', payload);
        if (result.success) successCount++;
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Week ${selectedWeek} schedule sent to ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'Failed to send schedule to webhooks' });
      }
    } catch (err) {
      console.error('Error sending schedule:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingSchedule(false);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    if (!selectedWeek) {
      setMessage({ type: 'error', text: 'Please select a week first' });
      return;
    }

    setSendingSchedule(true);
    setMessage({ type: '', text: '' });

    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);

      if (!games || games.length === 0) {
        setMessage({ type: 'error', text: 'No schedule data available' });
        setSendingSchedule(false);
        return;
      }

      const payload = formatScheduleForWebhook(games, selectedSeason, selectedWeek);

      const scheduleWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('schedule_updated')
      );

      if (scheduleWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for schedule updates' });
        setSendingSchedule(false);
        return;
      }

      let successCount = 0;
      for (const webhook of scheduleWebhooks) {
        const lastMessage = await webhooksService.getLastMessageForEvent(webhook.id, 'schedule_updated');
        if (lastMessage.success && lastMessage.log?.message_id) {
          const result = await webhooksService.sendWebhookMessage(
            webhook.id, 
            'schedule_updated', 
            payload, 
            lastMessage.log.message_id
          );
          if (result.success) successCount++;
        }
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Week ${selectedWeek} schedule updated on ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'No previous messages found to update' });
      }
    } catch (err) {
      console.error('Error updating schedule:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingSchedule(false);
    }
  };

  const handleSendScores = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    if (!selectedWeek) {
      setMessage({ type: 'error', text: 'Please select a week first' });
      return;
    }

    setSendingScores(true);
    setMessage({ type: '', text: '' });

    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);

      if (!games || games.length === 0) {
        setMessage({ type: 'error', text: 'No scores data available' });
        setSendingScores(false);
        return;
      }

      const payload = formatScoresForWebhook(games, selectedSeason, selectedWeek);

      const scoresWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('scores_updated')
      );

      if (scoresWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for scores updates' });
        setSendingScores(false);
        return;
      }

      let successCount = 0;
      for (const webhook of scoresWebhooks) {
        const result = await webhooksService.sendWebhookMessage(webhook.id, 'scores_updated', payload);
        if (result.success) successCount++;
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Week ${selectedWeek} scores sent to ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'Failed to send scores to webhooks' });
      }
    } catch (err) {
      console.error('Error sending scores:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingScores(false);
    }
  };

  const handleUpdateScores = async () => {
    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    if (!selectedWeek) {
      setMessage({ type: 'error', text: 'Please select a week first' });
      return;
    }

    setSendingScores(true);
    setMessage({ type: '', text: '' });

    try {
      const games = await scheduleService.getScheduleBySeason(selectedSeason.id);

      if (!games || games.length === 0) {
        setMessage({ type: 'error', text: 'No scores data available' });
        setSendingScores(false);
        return;
      }

      const payload = formatScoresForWebhook(games, selectedSeason, selectedWeek);

      const scoresWebhooks = webhooks.filter(w => 
        w.is_active && w.event_types.split(',').includes('scores_updated')
      );

      if (scoresWebhooks.length === 0) {
        setMessage({ type: 'error', text: 'No active webhooks found for scores updates' });
        setSendingScores(false);
        return;
      }

      let successCount = 0;
      for (const webhook of scoresWebhooks) {
        const lastMessage = await webhooksService.getLastMessageForEvent(webhook.id, 'scores_updated');
        if (lastMessage.success && lastMessage.log?.message_id) {
          const result = await webhooksService.sendWebhookMessage(
            webhook.id, 
            'scores_updated', 
            payload, 
            lastMessage.log.message_id
          );
          if (result.success) successCount++;
        }
      }

      if (successCount > 0) {
        setMessage({ type: 'success', text: `Week ${selectedWeek} scores updated on ${successCount} webhook(s)` });
      } else {
        setMessage({ type: 'error', text: 'No previous messages found to update' });
      }
    } catch (err) {
      console.error('Error updating scores:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingScores(false);
    }
  };

  return (
    <div className="webhooks-manager">
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="webhooks-header">
        <h2>Webhook Manager</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Webhook'}
        </button>
      </div>

      {showForm && (
        <div className="webhook-form-container">
          <form onSubmit={handleSubmit} className="webhook-form">
            <div className="form-group">
              <label>Webhook Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Discord News Channel"
                required
              />
            </div>

            <div className="form-group">
              <label>Webhook URL</label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://discordapp.com/api/webhooks/..."
                required
              />
            </div>

            <div className="form-group">
              <label>Webhook Type</label>
              <select name="type" value={formData.type} onChange={handleInputChange}>
                <option value="discord">Discord</option>
                <option value="slack">Slack</option>
                <option value="generic">Generic Webhook</option>
              </select>
            </div>

            <div className="form-group">
              <label>Event Types</label>
              <div className="event-types-list">
                {eventTypes.map(et => (
                  <label key={et.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.eventTypes.includes(et.value)}
                      onChange={() => handleEventTypeChange(et.value)}
                    />
                    {et.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isMediaWebhook"
                  checked={formData.isMediaWebhook}
                  onChange={handleInputChange}
                />
                {' '}Media/Article Webhook
              </label>
              <small>Enable this if this webhook is for publishing media articles to different channels</small>
            </div>

            {formData.isMediaWebhook && (
              <div className="form-group">
                <label>Channel Name</label>
                <input
                  type="text"
                  name="channel"
                  value={formData.channel}
                  onChange={handleInputChange}
                  placeholder="e.g., Highlights, News, Updates"
                />
                <small>The channel/category this webhook publishes to</small>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Saving...' : (selectedWebhookId ? 'Update' : 'Create')} Webhook
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && webhooks.length > 0 && (
        <div className="push-webhooks-section">
          <div className="season-selector">
            <label htmlFor="push-season-select">Select Season:</label>
            <select
              id="push-season-select"
              value={selectedSeason ? String(selectedSeason.id) : ''}
              onChange={(e) => setSelectedSeason(Number(e.target.value))}
            >
              <option value="">-- Select Season --</option>
              {availableSeasons.map(season => (
                <option key={season.id} value={String(season.id)}>
                  {season.name} {season.is_active ? '(Current)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="push-controls">
            <div className="push-group">
              <h4>📊 Rankings</h4>
              <div className="button-pair">
                <button 
                  onClick={handleSendRankings}
                  disabled={sendingRankings || !selectedSeason}
                  className="btn btn-success"
                  title="Send rankings as new message"
                >
                  {sendingRankings ? '📤...' : '📤 Send'}
                </button>
                <button 
                  onClick={handleUpdateRankings}
                  disabled={sendingRankings || !selectedSeason}
                  className="btn btn-info"
                  title="Update existing rankings message"
                >
                  {sendingRankings ? '🔄...' : '🔄 Update'}
                </button>
              </div>
            </div>

            <div className="push-group">
              <h4>📅 Schedule</h4>
              <select 
                value={selectedWeek || ''}
                onChange={(e) => setSelectedWeek(Number(e.target.value))}
                disabled={!selectedSeason || availableWeeks.length === 0}
                className="week-selector"
              >
                <option value="">-- Select Week --</option>
                {availableWeeks.map(week => (
                  <option key={week} value={week}>Week {week}</option>
                ))}
              </select>
              <div className="button-pair">
                <button 
                  onClick={handleSendSchedule}
                  disabled={sendingSchedule || !selectedSeason || !selectedWeek}
                  className="btn btn-success"
                  title="Send schedule as new message"
                >
                  {sendingSchedule ? '📤...' : '📤 Send'}
                </button>
                <button 
                  onClick={handleUpdateSchedule}
                  disabled={sendingSchedule || !selectedSeason || !selectedWeek}
                  className="btn btn-info"
                  title="Update existing schedule message"
                >
                  {sendingSchedule ? '🔄...' : '🔄 Update'}
                </button>
              </div>
            </div>

            <div className="push-group">
              <h4>🏆 Scores</h4>
              <select 
                value={selectedWeek || ''}
                onChange={(e) => setSelectedWeek(Number(e.target.value))}
                disabled={!selectedSeason || availableWeeks.length === 0}
                className="week-selector"
              >
                <option value="">-- Select Week --</option>
                {availableWeeks.map(week => (
                  <option key={week} value={week}>Week {week}</option>
                ))}
              </select>
              <div className="button-pair">
                <button 
                  onClick={handleSendScores}
                  disabled={sendingScores || !selectedSeason || !selectedWeek}
                  className="btn btn-success"
                  title="Send scores as new message"
                >
                  {sendingScores ? '📤...' : '📤 Send'}
                </button>
                <button 
                  onClick={handleUpdateScores}
                  disabled={sendingScores || !selectedSeason || !selectedWeek}
                  className="btn btn-info"
                  title="Update existing scores message"
                >
                  {sendingScores ? '🔄...' : '🔄 Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="webhooks-list">
        {loading && !showForm ? (
          <p>Loading webhooks...</p>
        ) : webhooks.length === 0 ? (
          <p>No webhooks configured yet. Create one to get started!</p>
        ) : (
          <table className="webhooks-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Events</th>
                <th>Status</th>
                <th>Last Triggered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map(webhook => (
                <tr key={webhook.id}>
                  <td className="webhook-name">{webhook.name}</td>
                  <td className="webhook-type">
                    <span className={`badge badge-${webhook.type}`}>{webhook.type}</span>
                  </td>
                  <td className="webhook-events">
                    {webhook.event_types.split(',').map(event => (
                      <span key={event} className="event-badge">
                        {event.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </td>
                  <td className="webhook-status">
                    <button
                      className={`status-toggle ${webhook.is_active ? 'active' : 'inactive'}`}
                      onClick={() => toggleWebhookStatus(webhook)}
                      title={webhook.is_active ? 'Active' : 'Inactive'}
                    >
                      {webhook.is_active ? '✓ Active' : '✗ Inactive'}
                    </button>
                  </td>
                  <td className="webhook-last-triggered">
                    {webhook.last_triggered_at 
                      ? new Date(webhook.last_triggered_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Never'
                    }
                  </td>
                  <td className="webhook-actions">
                    <button
                      className="btn btn-small btn-info"
                      onClick={() => handleEdit(webhook)}
                      title="Edit webhook"
                    >
                      ✎
                    </button>
                    <button
                      className="btn btn-small btn-success"
                      onClick={() => openMessageSender(webhook.id, webhook.event_types.split(',')[0])}
                      title="Send message"
                    >
                      💬
                    </button>
                    <button
                      className="btn btn-small btn-warning"
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testing === webhook.id}
                      title="Test webhook"
                    >
                      {testing === webhook.id ? '...' : '⚡'}
                    </button>
                    <button
                      className="btn btn-small btn-info"
                      onClick={() => handleViewLogs(webhook.id)}
                      title="View logs"
                    >
                      📋
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(webhook.id)}
                      title="Delete webhook"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showLogs && (
        <div className="logs-modal">
          <div className="logs-modal-content">
            <div className="logs-header">
              <h3>Webhook Logs</h3>
              <button className="close-btn" onClick={() => setShowLogs(false)}>✕</button>
            </div>
            
            <div className="logs-table-container">
              {logs.length === 0 ? (
                <p>No logs available for this webhook</p>
              ) : (
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Status</th>
                      <th>Response Code</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id} className={log.success ? 'success' : 'error'}>
                        <td>{log.event_type}</td>
                        <td>{log.success ? '✓ Success' : '✗ Failed'}</td>
                        <td>{log.status_code || 'N/A'}</td>
                        <td>{new Date(log.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showMessageSender && (
        <div className="message-sender-modal">
          <div className="message-sender-content">
            <div className="message-sender-header">
              <h3>{messageSenderData.editMessageId ? 'Edit Message' : 'Send New Message'}</h3>
              <button className="close-btn" onClick={() => { setShowMessageSender(false); resetMessageSender(); }}>✕</button>
            </div>
            
            <form onSubmit={handleSendMessage} className="message-sender-form">
              <div className="form-group">
                <label>Event Type</label>
                <select 
                  value={messageSenderData.eventType}
                  onChange={(e) => setMessageSenderData(prev => ({ ...prev, eventType: e.target.value }))}
                >
                  {eventTypes.map(et => (
                    <option key={et.value} value={et.value}>{et.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={messageSenderData.title}
                  onChange={(e) => setMessageSenderData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Message title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={messageSenderData.description}
                  onChange={(e) => setMessageSenderData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Message description"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  value={messageSenderData.image}
                  onChange={(e) => setMessageSenderData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="form-group">
                <label>Fields</label>
                {messageSenderData.eventType === 'rankings_updated' && (
                  <p className="field-hint">For rankings, use: RNKP, TEAM, W, L, PCT, PF, PA, DIFF</p>
                )}
                <div className="fields-list">
                  {messageSenderData.fields.map((field, idx) => (
                    <div key={idx} className="field-row">
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                        placeholder="Field name"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => handleFieldChange(idx, 'value', e.target.value)}
                        placeholder="Field value"
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() => handleRemoveField(idx)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  onClick={handleAddField}
                >
                  + Add Field
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? 'Sending...' : (messageSenderData.editMessageId ? 'Update Message' : 'Send Message')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowMessageSender(false); resetMessageSender(); }}>
                  Cancel
                </button>
              </div>

              {messageSenderData.editMessageId && (
                <p className="info-text">ℹ️ This will update the last sent message for this event type.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksManager;
